import React, { useEffect, useRef, useState } from "react";
import { CarCompany } from "utils/modals";
import MUIDataTable from "mui-datatables";
import { getTableTheme } from "../tableTheme";
import USER_IMG from "assets/images/user-image.png";
import { commonTableStyles as styles, TABLE_TOKENS } from "../styles";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { Box, ThemeProvider, Typography } from "@mui/material";
import DeleteEditModal from "components/customModal/DeleteEditModal";
import ContainedButton from "components/button/contained-button";
import "../style.css";

interface Props {
  searchHandler: any;
  currentPage: number;
  rowsPerPage: number;
  totalRecords: number;
  onEditPress: Function;
  onDeletePress: Function;
  onAddPress?: () => void;
  handleChangePage: Function;
  handleChangeRowsPerPage: Function;
  carCompaniesList: Array<CarCompany>;
}

const SEARCH_DEBOUNCE_MS = 500;

const CarCompanyTable = (props: Props) => {
  const { carCompaniesList, searchHandler, onAddPress } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteValue] = useState<any>({});
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSearchRef = useRef("");

  const clearPendingSearch = () => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
  };

  useEffect(() => clearPendingSearch, []);

  const applySearch = (text: string) => {
    lastSearchRef.current = text;
    searchHandler(text);
  };

  const handleSearchChange = (text: string | null) => {
    const nextText = `${text || ""}`.trim();
    clearPendingSearch();
    if (nextText === lastSearchRef.current) return;
    searchTimerRef.current = setTimeout(
      () => applySearch(nextText),
      SEARCH_DEBOUNCE_MS
    );
  };

  const columns: any = [
    {
      name: "id",
      label: "S. No.",
      options: {
        sort: false,
        filter: false,
        customBodyRender: (_value: any, metaData: any) => {
          return <div>{metaData.rowIndex + 1 || "N/A"}</div>;
        },
      },
    },
    {
      name: "logo",
      label: "Logo",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any) => {
          return (
            <img
              style={styles.profileIcon}
              src={value || USER_IMG}
              alt="icon-img"
            />
          );
        },
      },
    },
    {
      name: "name",
      label: "Companies",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any) => {
          return <div>{value || "N/A"}</div>;
        },
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: (_: any, tableMeta: any) => {
          return (
            <div className="icon-container icon-container-2">
              <BorderColorIcon
                style={{ color: TABLE_TOKENS.accent, fontSize: "1.3rem" }}
                onClick={() => {
                  props.onEditPress(tableMeta?.tableData[tableMeta?.rowIndex]);
                }}
              />
            </div>
          );
        },
      },
    },
  ];

  const options: any = {
    print: false,
    filter: false,
    download: false,
    serverSide: true,
    viewColumns: false,
    selectableRows: "none",
    search: true,
    customSearch: () => true,
    onSearchChange: (text: string | null) => handleSearchChange(text),
    onSearchClose: () => {
      clearPendingSearch();
      if (lastSearchRef.current) applySearch("");
    },
    customToolbar: () =>
      onAddPress ? (
        <ContainedButton
          externalStyles={styles.toolbarAddBtn}
          onClick={onAddPress}
          title="Add company"
        />
      ) : null,
    count: props?.totalRecords,
    page: props?.currentPage - 1,
    rowsPerPage: props.rowsPerPage || 10,
    onChangePage: (value: number) => props.handleChangePage(value),
    onChangeRowsPerPage: (value: any) => props.handleChangeRowsPerPage(value),
    rowsPerPageOptions: [10, 20, 50, 100],
    searchPlaceholder: " Enter Company Name .",
  };
  const TableComponent: any = MUIDataTable;

  return (
    <Box>
      <Box sx={styles.paper}>
        <ThemeProvider theme={getTableTheme()}>
          <TableComponent
            title={
              <Typography variant="h5" sx={styles.tableHeadingTxt}>
                {`Companies List`}
              </Typography>
            }
            data={carCompaniesList}
            columns={columns}
            options={options}
          />
        </ThemeProvider>
        <DeleteEditModal
          onClose={() => setModalOpen(false)}
          title="Delete Confirmation"
          children={"Are you sure want to delete this selected Car Company"}
          visible={modalOpen}
          onConfirm={() => props.onDeletePress(deleteValue)}
        />
      </Box>
    </Box>
  );
};

export default CarCompanyTable;
